import { useEffect, useRef, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { getDesktopDeepLinkUrl } from "@/lib/desktop-deep-link";
import { AuthPageLayout } from "./auth-page-layout";
import { verifyEmail } from "@/api/auth";
import { getAuthErrorMessage, useAuth } from "@/hooks/use-auth";
import {
  getDesktopHandoffNext,
  popPendingNext,
  withNext,
} from "./next-redirect";
import { FormError, Spinner } from "@/components/ui";
import { buttonVariants } from "@/components/ui/button";
import { useT } from "@/i18n";
import { authMessages } from "@/i18n/messages/auth";
import { commonMessages } from "@/i18n/messages/common";

export function VerifyEmailPage() {
  const t = useT(authMessages);
  const tc = useT(commonMessages);
  const { key } = useParams<{ key: string }>();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nativeReturn = params.get("desktop") === "1";
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState<"verifying" | "success" | "error">(
    "verifying",
  );
  const [error, setError] = useState("");
  const verification = useRef<{
    key: string;
    promise: ReturnType<typeof verifyEmail>;
  } | null>(null);
  // Reclaim the desktop bridge URL stashed during /auth/register so the
  // round-trip completes back at the Tauri pair endpoint. Read once —
  // popPendingNext clears the entry.
  const [nextUrl, setNextUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!key) {
      setStatus("error");
      return;
    }

    let cancelled = false;
    setStatus("verifying");
    // A confirmation is single-use. Reuse this key's in-flight request when
    // StrictMode replays effects or the auth context refreshes.
    if (verification.current?.key !== key) {
      verification.current = { key, promise: verifyEmail(key) };
    }
    const pending = verification.current.promise;
    void (async () => {
      try {
        await pending;
      } catch (err) {
        if (cancelled) return;
        setStatus("error");
        setError(getAuthErrorMessage(err));
        return;
      }
      if (cancelled) return;
      if (nativeReturn) {
        setStatus("success");
        void refreshUser().catch(() => {});
        return;
      }
      // The verify endpoint established a session — sync the auth context
      // so GuestGuard on /auth/login sees it and forwards us: to the app,
      // or through the desktop pairing bridge when a `next` is pending.
      await refreshUser();
      if (cancelled) return;
      const next = popPendingNext() ?? getDesktopHandoffNext();
      setNextUrl(next);
      setStatus("success");
      navigate(withNext("/auth/login", next), { replace: true });
    })();
    return () => {
      cancelled = true;
    };
  }, [key, navigate, refreshUser, nativeReturn]);

  return (
    <AuthPageLayout>
      {status === "verifying" && (
        <div className="auth-state-stack py-8" data-testid="verify-loading">
          <Spinner className="h-8 w-8" />
          <p className="auth-page-copy">{t("verifying")}</p>
        </div>
      )}

      {status === "success" && (
        <div className="auth-state-stack" data-testid="verify-success">
          <h2 className="auth-page-title">{t("emailVerified")}</h2>
          <p className="auth-page-copy">
            {nativeReturn ? t("returnToContinue") : t("signingYouIn")}
          </p>
          {nativeReturn ? (
            <a
              href={getDesktopDeepLinkUrl("email-verified")}
              className={buttonVariants({ variant: "secondary", size: "lg" })}
              rel="noreferrer"
              data-testid="verify-return-native"
            >
              {t("returnToDenker")}
            </a>
          ) : (
            <Link
              to={withNext("/auth/login", nextUrl)}
              className={buttonVariants({
                variant: "secondary",
                size: "lg",
                className: "auth-liquid-primary-button w-full font-semibold",
              })}
              data-testid="verify-login-link"
            >
              {tc("continue")}
            </Link>
          )}
        </div>
      )}

      {status === "error" && (
        <div className="auth-state-stack" data-testid="verify-error">
          <h2 className="auth-page-title">{t("linkExpired")}</h2>
          <FormError
            message={key ? error : t("invalidVerificationLink")}
            className="mt-0 w-full justify-center"
          />
          {nativeReturn ? (
            <>
              <p className="auth-page-copy">{t("verifyErrorNative")}</p>
              <a
                href={getDesktopDeepLinkUrl("email-verified")}
                className="auth-small-link"
                rel="noreferrer"
                data-testid="verify-error-return-native"
              >
                {t("returnToDenker")}
              </a>
            </>
          ) : (
            <Link
              to="/auth/login"
              className="auth-small-link"
              data-testid="verify-error-login-link"
            >
              {t("signIn")}
            </Link>
          )}
        </div>
      )}
    </AuthPageLayout>
  );
}
